import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { RefreshCw, ArrowUpRight, Plus, X, Trash2, Clock, PanelRightClose, PanelRightOpen } from 'lucide-react'
import { ArmedProvider, useArmed, confirmDestructive } from '../components/destructive'
import SubstantiveFlow from '../components/SubstantiveFlow'
import { FILING_STORY } from '../pipelineFlow'
import {
  fetchStepOutdatedCounts,
  markResultsStale,
  me,
  promotePromptVersion,
  promptSlots as slotsApi,
  promptVersions,
  results as resultsApi,
  runStale,
  stepDeps as stepDepsApi,
  steps as stepsApi,
  type ComputationStep,
  type PromptSlot,
  type PromptVersion,
  type StepStaleCount,
} from '../api'

// Ken's node names run long ("Decide to File Email-based 'Issue' with
// Existing Matter or Instantiate New Matter" is ~80 chars). Use a taller
// node + word-wrap inside a foreignObject so multi-line titles fit
// without truncating.
const NODE_W = 168   // rounded-rect width — reduced 25% from 224 (Ken 2026-08-12); names may wrap to 4 lines, which is fine
const NODE_H = 104
const X_GAP = 40   // horizontal room between node rectangles — halved from 80 (Ken 2026-08-12)
const Y_GAP = 24
const PADDING = 36

type StepState = 'idle' | 'fresh' | 'stale' | 'mixed'

// ── Render-only code-step overlay (Ken 2026-09-07) ───────────────────────────
// The "code-steps" view is the SAME vertical DAG view — same renderer, same
// design — with two additions, both purely visual:
//   1. every non-LLM CODE step gets a green border + a "CODE" flag, and
//   2. the four code steps that are NOT seeded as DAG nodes are injected as
//      render-only nodes (dashed border, "NOT IN DAG" flag), placed in their
//      approximate execution level.
// This touches NO DAG code or metadata. The injected nodes are synthetic and
// carry negative ids so they never collide with real steps or the inspector.
const isCodeStepName = (name: string) => {
  const n = name.toLowerCase()
  return (
    n.includes('validate email obligation') ||   // Validate Email Obligations (L5, pure code)
    n.includes('decide to file') || n.includes('instantiate new matter') ||  // Decide (L5)
    (n.includes('classify') && n.includes('attachment'))  // Classify Email Attachment (L3, code-first)
  )
}
const synthCodeStep = (id: number, name: string, level: number): ComputationStep => ({
  id, name, level, slot: 0, slot_name: '', prompt_version_label: '', backend: 'code',
  fanout_kind: 'none', parent_ids: [], child_ids: [], created_at: '',
})
// The four code steps that run but are not seeded as DAG nodes, at their
// approximate execution level (easy to nudge — these are render-only guesses).
const INJECTED_CODE_STEPS: ComputationStep[] = [
  synthCodeStep(-101, 'Deduplicate Email', 1),
  synthCodeStep(-102, 'Extract Attachment Content', 2),
  synthCodeStep(-103, 'Link Referenced Google Docs', 3),
  synthCodeStep(-104, 'Route Accounting Email', 4),
]
const INJECTED_IDS = new Set(INJECTED_CODE_STEPS.map((s) => s.id))

// Contract-checklist overlay (Emerson 2026-09-27). We do NOT replace L1: the
// existing "Classify Email …" node is HIGHLIGHTED purple in this view (an ANCHOR)
// — it gains a "detect sender email → candidate submitter [via firm_lexicon]"
// addition, and BRANCHES (purple edge) into the new steps. The genuinely-NEW
// steps below are PURPLE synthetic nodes drawn on top of the intact real
// pipeline, non-clickable, chained via child_ids. Negative ids never collide;
// levels are approximate render guesses (easy to nudge).
const CC_PURPLE = '#8b5cf6'   // new / proposed steps
const CC_YELLOW = '#ca8a04'   // reused existing document-read steps (incl. the code step not in the general DAG)
// The existing L1 classify step we anchor onto — matched by name ("Classify Email
// as Personal, Digest"), NOT the attachment classifier.
const isL1ClassifyName = (name: string) => {
  const n = name.toLowerCase()
  return n.includes('classify') && (n.includes('personal') || n.includes('digest'))
}
const synthCCStep = (id: number, name: string, level: number, children: number[] = []): ComputationStep => ({
  id, name, level, slot: 0, slot_name: '', prompt_version_label: '', backend: 'code',
  fanout_kind: 'none', parent_ids: [], child_ids: children, created_at: '',
})
const CC_ANCHOR_CHILD = -202  // L1 branches (purple edge) into Verify Submitter Identity
const CC_INJECTED_STEPS: ComputationStep[] = [
  synthCCStep(-202, 'Verify Submitter Identity', 2, [-207]),
  // Once we know the submitter, read the doc + its markup FIRST — so Universal
  // Intake can check Word-format + "already read/commented" and continue-or-advise.
  synthCCStep(-207, 'Extract Document In Full', 3, [-208]),
  synthCCStep(-208, 'Extract Document Markup', 4, [-203]),
  synthCCStep(-203, 'Step 1 — Universal Intake', 5, [-204]),
  synthCCStep(-204, 'Step 2 — Type-Scoped Checks', 6, [-205]),
  synthCCStep(-205, 'Step 3 — Compile & Handoff', 7, [-206]),
  synthCCStep(-206, 'Contract Submission Record', 8, []),
]
const CC_IDS = new Set(CC_INJECTED_STEPS.map((s) => s.id))
const CC_YELLOW_IDS = new Set([-207, -208])  // existing doc-read steps surfaced in the checklist sequence

export default function PipelinePage() {
  return (
    <ArmedProvider scope="pipeline">
      <PipelinePageInner />
    </ArmedProvider>
  )
}

function PipelinePageInner() {
  const qc = useQueryClient()
  const steps = useQuery({ queryKey: ['steps'], queryFn: () => stepsApi.list() })
  // (Was a paginated /api/results/ fetch used for header counts + per-step
  // state; replaced by the dedicated outdated-counts endpoint above.)
  // Look up the current user's tenant once so outdated-count + mark-stale
  // calls can scope to the right client without an extra picker UI.
  const meQuery = useQuery({ queryKey: ['me'], queryFn: () => me() })
  const clientCode = meQuery.data?.client?.code
  const outdatedCounts = useQuery({
    queryKey: ['step-outdated-counts', clientCode],
    queryFn: () => fetchStepOutdatedCounts(clientCode),
    enabled: !!clientCode,
  })
  const outdatedByStepId = useMemo(() => {
    const out: Record<number, StepStaleCount> = {}
    for (const s of outdatedCounts.data?.steps ?? []) out[s.step_id] = s
    return out
  }, [outdatedCounts.data])
  const [selectedId, setSelectedId] = useState<number | null>(null)
  // Collapsible step-inspector (Ken 2026-08-12) — reclaim the 420px column for
  // the graph. Persisted so the choice sticks across visits.
  const [inspectorOpen, setInspectorOpen] = useState<boolean>(() => {
    try { return localStorage.getItem('me_pipeline_inspector') !== '0' } catch { return true }
  })
  const toggleInspector = () => setInspectorOpen((v) => {
    const nv = !v
    try { localStorage.setItem('me_pipeline_inspector', nv ? '1' : '0') } catch { /* ignore */ }
    return nv
  })
  // Graph orientation (Ken 2026-08-12): horizontal (left→right, the original)
  // or vertical (top→down). Persisted.
  type PipelineView = 'horizontal' | 'vertical' | 'substantive' | 'substantive2' | 'substantive3' | 'substantive4' | 'codesteps' | 'contractchecklist'
  const [view, setView] = useState<PipelineView>(() => {
    try {
      const v = localStorage.getItem('me_pipeline_view')
      return (['vertical', 'substantive', 'substantive2', 'substantive3', 'substantive4', 'codesteps', 'contractchecklist'].includes(v || '')
        ? (v as PipelineView)
        : 'horizontal')
    } catch { return 'horizontal' }
  })
  const changeView = (v: PipelineView) => {
    setView(v)
    try { localStorage.setItem('me_pipeline_view', v) } catch { /* ignore */ }
  }

  const runStaleMu = useMutation({
    mutationFn: () => runStale(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['step-outdated-counts'] })
      qc.invalidateQueries({ queryKey: ['runs-count'] })
      qc.invalidateQueries({ queryKey: ['runs'] })
    },
  })

  // Per-step state. Source of truth is the dedicated outdated-counts
  // endpoint (server-aggregated counts of total / stale / outdated for
  // BOTH ComputationResult and MatterStepResult per step). Earlier
  // versions of this page derived totals from a paginated /api/results/
  // fetch — that capped at 50 rows so step counts shown in the UI did
  // NOT reflect CLI / celery / overnight runs that produced thousands.
  const stateByStep = useMemo<Record<number, { state: StepState; total: number; stale: number; failed: number }>>(() => {
    const out: Record<number, { state: StepState; total: number; stale: number; failed: number }> = {}
    for (const s of steps.data || []) out[s.id] = { state: 'idle', total: 0, stale: 0, failed: 0 }
    for (const od of outdatedCounts.data?.steps ?? []) {
      const e = out[od.step_id]
      if (!e) continue
      e.total = od.computation_result.total + od.matter_step_result.total
      e.stale = od.computation_result.stale + od.matter_step_result.stale
    }
    for (const id in out) {
      const e = out[id]
      if (e.total === 0) e.state = 'idle'
      else if (e.stale > 0 && e.stale < e.total) e.state = 'mixed'
      else if (e.stale > 0) e.state = 'stale'
      else e.state = 'fresh'
    }
    return out
  }, [steps.data, outdatedCounts.data])

  const layout = useMemo(
    () => computeLayout(
      view === 'codesteps' ? [...(steps.data ?? []), ...INJECTED_CODE_STEPS]
        : view === 'contractchecklist'
          ? [
              // Anchor: give the existing L1 classify step a child edge into the
              // first new step, so the branch reads out of the real pipeline.
              ...(steps.data ?? []).map((s) =>
                isL1ClassifyName(s.name) ? { ...s, child_ids: [...s.child_ids, CC_ANCHOR_CHILD] } : s),
              ...CC_INJECTED_STEPS,
            ]
        : (steps.data ?? []),
      view === 'vertical' || view === 'codesteps' ? 'vertical' : 'horizontal',
    ),
    [steps.data, view],
  )
  const selected = (steps.data ?? []).find((s) => s.id === selectedId) ?? null
  const ancestors = selected ? collectAncestors(selected.id, steps.data ?? []) : new Set<number>()
  const descendants = selected ? collectDescendants(selected.id, steps.data ?? []) : new Set<number>()

  const totalStale = Object.values(stateByStep).reduce((a, e) => a + e.stale, 0)
  const { AdminArmingToggle } = useArmed()

  return (
    <div
      className="grid h-full gap-px bg-[color:var(--me-border)]"
      style={{ gridTemplateColumns: inspectorOpen ? '1fr 420px' : '1fr' }}
    >
      <section className="me-scroll overflow-auto bg-[color:var(--me-surface)] p-6">
        <div className="mb-3 flex items-center gap-3 text-[12px] me-text-dim">
          {/* View pull-down (top-left). Counts + Legend hidden per Ken
              2026-08-12 — the header is just the view switch + actions now. */}
          <label className="inline-flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-wider me-text-mute">View</span>
            <select
              value={view}
              onChange={(e) => changeView(e.target.value as PipelineView)}
              className="me-input text-[12px] py-1"
            >
              <option value="horizontal">horizontal</option>
              <option value="vertical">vertical</option>
              <option value="substantive">substantive-1: filing</option>
              <option value="substantive2">substantive-2: filing (vertical)</option>
              <option value="substantive3">substantive-3: filing (vertical, bottom-up)</option>
              <option value="substantive4">substantive-4: filing (compact, bottom-up)</option>
              <option value="codesteps">code-steps (vertical, with hidden code)</option>
              <option value="contractchecklist">contract checklist (proposed)</option>
            </select>
          </label>
          {totalStale > 0 && (
            <button
              onClick={() => runStaleMu.mutate()}
              disabled={runStaleMu.isPending}
              className="me-btn me-btn-outline"
            >
              <RefreshCw size={12} />
              {runStaleMu.isPending ? 'Enqueuing…' : `Run ${totalStale} stale`}
            </button>
          )}
          {runStaleMu.data && (
            <span className="me-chip me-chip-fresh">enqueued {runStaleMu.data.enqueued}</span>
          )}
          <span className="ml-auto" />
          <button
            onClick={toggleInspector}
            className="me-btn me-btn-outline"
            title={inspectorOpen ? 'Collapse the step inspector for more graph room' : 'Show the step inspector'}
          >
            {inspectorOpen ? <PanelRightClose size={12} /> : <PanelRightOpen size={12} />}
            {inspectorOpen ? 'Hide inspector' : 'Inspector'}
          </button>
          <AdminArmingToggle />
        </div>

        {view.startsWith('substantive') ? (
          <SubstantiveFlow
            story={FILING_STORY}
            orientation={view === 'substantive3' || view === 'substantive4' ? 'verticalUp' : view === 'substantive2' ? 'vertical' : 'horizontal'}
            compact={view === 'substantive4'}
          />
        ) : (
        <div className="me-card mt-3 overflow-auto p-3">
          {/* Vertical view needs a wider LEFT gutter for the row-side "Level N"
              labels; horizontal keeps the symmetric padding. */}
          <svg
            width={layout.width + PADDING + (layout.vertical ? 64 : PADDING)}
            height={layout.height + PADDING * 2}
            className="block"
          >
            <g transform={`translate(${layout.vertical ? 64 : PADDING},${PADDING})`}>
              {Array.from({ length: layout.levels }, (_, i) => (
                <text
                  key={i}
                  // Horizontal: label above each column. Vertical: label to the
                  // left of each row, right-aligned into the padding gutter.
                  x={layout.vertical ? -10 : i * (NODE_W + X_GAP) + NODE_W / 2}
                  y={layout.vertical ? i * (NODE_H + Y_GAP) + NODE_H / 2 : -12}
                  textAnchor={layout.vertical ? 'end' : 'middle'}
                  dominantBaseline={layout.vertical ? 'middle' : 'auto'}
                  className="fill-[color:var(--me-textMute)] text-[10px] uppercase"
                  style={{ letterSpacing: '0.08em' }}
                >
                  Level {i}
                </text>
              ))}
              {layout.edges.map((e, i) => {
                const isHighlight =
                  selected &&
                  ((selected.id === e.parentId && descendants.has(e.childId)) ||
                    (selected.id === e.childId && ancestors.has(e.parentId)))
                const isCCedge = view === 'contractchecklist' && CC_IDS.has(e.childId)
                return (
                  <path
                    key={i}
                    d={e.path}
                    fill="none"
                    stroke={isCCedge ? (CC_YELLOW_IDS.has(e.childId) ? CC_YELLOW : CC_PURPLE) : isHighlight ? 'var(--me-fg)' : 'var(--me-borderStrong)'}
                    strokeWidth={isHighlight || isCCedge ? 1.6 : 1}
                  />
                )
              })}
              {layout.nodes.map((n) => {
                const stateInfo = stateByStep[n.step.id] || { state: 'idle' as StepState, total: 0, stale: 0, failed: 0 }
                const isSelected = n.step.id === selectedId
                const isRelated = ancestors.has(n.step.id) || descendants.has(n.step.id)
                const fill =
                  isSelected ? 'var(--me-fg)' :
                  stateInfo.state === 'stale' ? 'var(--me-staleBg)' :
                  stateInfo.state === 'fresh' ? 'var(--me-freshBg)' :
                  stateInfo.state === 'mixed' ? 'var(--me-staleBg)' :
                  'var(--me-surface)'
                const stroke =
                  isSelected ? 'var(--me-fg)' :
                  stateInfo.state === 'stale' ? 'var(--me-stale)' :
                  stateInfo.state === 'fresh' ? 'var(--me-fresh)' :
                  stateInfo.state === 'mixed' ? 'var(--me-stale)' :
                  isRelated ? 'var(--me-fg)' : 'var(--me-borderStrong)'
                const textColor = isSelected ? 'var(--me-surface)' : 'var(--me-text)'
                const subColor = isSelected ? 'var(--me-surface)' :
                  stateInfo.state === 'stale' ? 'var(--me-stale)' :
                  stateInfo.state === 'fresh' ? 'var(--me-fresh)' :
                  'var(--me-textMute)'
                // code-steps view only: flag CODE steps green; injected
                // (not-in-DAG) nodes get a dashed border and are non-clickable.
                const codeMode = view === 'codesteps'
                const isInjected = codeMode && INJECTED_IDS.has(n.step.id)
                const isCode = codeMode && (isInjected || isCodeStepName(n.step.name))
                const effStroke = isCode && !isSelected ? 'var(--me-fresh)' : stroke
                // contract-checklist overlay: the proposed new steps outline PURPLE,
                // are non-clickable, and carry a "proposed" flag.
                const isCC = view === 'contractchecklist' && CC_IDS.has(n.step.id)
                const isCCyellow = view === 'contractchecklist' && CC_YELLOW_IDS.has(n.step.id)
                // The existing L1 classify node is highlighted purple (anchor) but
                // stays a real, clickable step — we ADD to it, not replace it.
                const isCCAnchor = view === 'contractchecklist' && isL1ClassifyName(n.step.name)
                const finalStroke = isCCyellow ? CC_YELLOW : (isCC || isCCAnchor) ? CC_PURPLE : effStroke
                return (
                  <g
                    key={n.step.id}
                    transform={`translate(${n.x},${n.y})`}
                    className={isInjected || isCC ? undefined : 'cursor-pointer'}
                    onClick={isInjected || isCC ? undefined : () => setSelectedId(n.step.id)}
                  >
                    <rect
                      width={NODE_W}
                      height={NODE_H}
                      rx={8}
                      fill={fill}
                      stroke={finalStroke}
                      strokeWidth={isSelected ? 1.8 : isCC || isCCAnchor || isCode ? 1.6 : 1.2}
                      strokeDasharray={isInjected ? '5 3' : undefined}
                    />
                    {isCode && (
                      <text
                        x={9}
                        y={15}
                        className="fill-[color:var(--me-fresh)] uppercase"
                        style={{ fontSize: 8, letterSpacing: '0.09em', fontWeight: 600 }}
                      >
                        {isInjected ? 'code · not in DAG' : 'code'}
                      </text>
                    )}
                    {isCC && !isCCyellow && (
                      <text
                        x={9}
                        y={15}
                        className="uppercase"
                        style={{ fontSize: 8, letterSpacing: '0.09em', fontWeight: 600, fill: CC_PURPLE }}
                      >
                        proposed · contract checklist
                      </text>
                    )}
                    {isCCyellow && (
                      <text
                        x={9}
                        y={15}
                        className="uppercase"
                        style={{ fontSize: 8, letterSpacing: '0.09em', fontWeight: 600, fill: CC_YELLOW }}
                      >
                        reused · document read
                      </text>
                    )}
                    {isCCAnchor && (
                      <text
                        x={9}
                        y={15}
                        className="uppercase"
                        style={{ fontSize: 8, letterSpacing: '0.09em', fontWeight: 600, fill: CC_PURPLE }}
                      >
                        + contract-checklist
                      </text>
                    )}
                    <foreignObject x={0} y={0} width={NODE_W} height={NODE_H}>
                      <div
                        // Real word-wrap (SVG <text> can't wrap) so Ken's
                        // long prose node names sit within the box.
                        style={{
                          width: '100%',
                          height: '100%',
                          padding: '8px 10px',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          textAlign: 'center',
                          boxSizing: 'border-box',
                          overflow: 'hidden',
                          gap: 2,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 11.5,
                            fontWeight: 500,
                            lineHeight: 1.2,
                            color: textColor,
                            wordBreak: 'normal',
                            overflowWrap: 'anywhere',
                          }}
                        >
                          {n.step.name}
                        </div>
                        {(() => {
                          const od = outdatedByStepId[n.step.id]
                          const totalOutdated = od
                            ? od.computation_result.outdated + od.matter_step_result.outdated
                            : 0
                          // "no runs yet" label removed (Ken 2026-08-12) — an
                          // un-run step now shows just its name, no sub-label.
                          const noRuns = stateInfo.total === 0 && (!od || (od.computation_result.total + od.matter_step_result.total === 0))
                          if (noRuns) return null
                          const parts = [`results: ${stateInfo.total}`]
                          if (stateInfo.stale > 0) parts.push(`stale: ${stateInfo.stale}`)
                          if (totalOutdated > 0) parts.push(`outdated: ${totalOutdated}`)
                          return (
                            <div
                              style={{
                                fontSize: 9.5,
                                lineHeight: 1.2,
                                color: subColor,
                                opacity: isSelected ? 0.78 : 1,
                                overflowWrap: 'anywhere',
                              }}
                            >
                              {parts.join(' · ')}
                            </div>
                          )
                        })()}
                      </div>
                    </foreignObject>
                  </g>
                )
              })}
            </g>
          </svg>
        </div>
        )}
      </section>

      {inspectorOpen && (
        <aside className="me-scroll overflow-auto bg-[color:var(--me-surface)] p-5">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-wider me-text-mute">Inspector</span>
            <button
              onClick={toggleInspector}
              className="me-text-mute hover:me-text inline-flex items-center"
              title="Collapse the inspector"
            >
              <PanelRightClose size={13} />
            </button>
          </div>
          {selected ? (
            <StepDetail
              step={selected}
              steps={steps.data ?? []}
              stateInfo={stateByStep[selected.id]}
              outdated={outdatedByStepId[selected.id]}
              clientCode={clientCode}
              onChange={() => {
                qc.invalidateQueries({ queryKey: ['step-outdated-counts'] })
        qc.invalidateQueries({ queryKey: ['runs-count'] })
                qc.invalidateQueries({ queryKey: ['steps'] })
                qc.invalidateQueries({ queryKey: ['step-outdated-counts'] })
              }}
            />
          ) : (
            <div className="text-[12px] me-text-mute">Click a step to inspect it.</div>
          )}
        </aside>
      )}
    </div>
  )
}

function StepDetail({
  step,
  steps,
  stateInfo,
  outdated,
  clientCode,
  onChange,
}: {
  step: ComputationStep
  steps: ComputationStep[]
  stateInfo?: { state: StepState; total: number; stale: number; failed: number }
  outdated?: StepStaleCount
  clientCode?: string
  onChange: () => void
}) {
  const versions = useQuery({
    queryKey: ['versions', 'slot', step.slot],
    queryFn: () => promptVersions.bySlot(step.slot),
  })
  const recent = useQuery({
    queryKey: ['results', 'step', step.id],
    queryFn: () => resultsApi.list({ step: step.id }),
  })

  const productionVersion = useMemo(
    () => (versions.data || []).find((v) => v.labels.includes(step.prompt_version_label)),
    [versions.data, step.prompt_version_label],
  )
  const otherVersions = useMemo(
    () => (versions.data || []).filter((v) => v.id !== productionVersion?.id),
    [versions.data, productionVersion],
  )

  const promote = useMutation({
    mutationFn: (versionId: number) => promotePromptVersion(versionId, step.prompt_version_label),
    onSuccess: () => {
      versions.refetch()
      recent.refetch()
      onChange()
    },
  })


  return (
    <div>
      <div className="me-section-h">Step</div>
      <h3 className="text-[15px] font-medium me-text">{step.name}</h3>
      <div className="mt-1 flex flex-wrap items-center gap-1.5">
        <span className="me-chip">L{step.level}</span>
        <span className="me-chip">{step.slot_name}</span>
        <span className="me-chip me-chip-fresh">{step.prompt_version_label}</span>
        <span className="me-chip">{step.backend}</span>
        {stateInfo && stateInfo.total > 0 && (
          <span className={`me-chip ${stateInfo.state === 'fresh' ? 'me-chip-fresh' : stateInfo.state === 'stale' ? 'me-chip-stale' : ''}`}>
            {stateInfo.total} results · {stateInfo.stale} stale
          </span>
        )}
      </div>

      {productionVersion && (
        <div className="mt-5">
          <div className="me-section-h">Production prompt · v{productionVersion.version}</div>
          <pre className="me-mono whitespace-pre-wrap rounded-md bg-[color:var(--me-panel)] p-3 text-[11.5px] leading-relaxed me-text max-h-48 overflow-auto">
            {productionVersion.content}
          </pre>
          {productionVersion.commit_message && (
            <div className="mt-1 text-[11px] me-text-mute italic">{productionVersion.commit_message}</div>
          )}
        </div>
      )}

      <ArmedDagEditor step={step} steps={steps} onChange={onChange} />

      <OutdatedPanel step={step} outdated={outdated} clientCode={clientCode} onChange={onChange} />

      {otherVersions.length > 0 && (
        <div className="mt-5">
          <div className="me-section-h">Other versions</div>
          <ul className="space-y-1">
            {otherVersions.slice(0, 5).map((v) => (
              <PromoteRow key={v.id} v={v} step={step} promote={promote} />
            ))}
          </ul>
        </div>
      )}

      <div className="mt-5">
        <div className="me-section-h">Recent results</div>
        {recent.isLoading && <div className="text-[12px] me-text-mute">loading…</div>}
        <ul className="space-y-1">
          {(recent.data ?? []).slice(0, 6).map((r) => (
            <li key={r.id} className="me-card p-2.5">
              <div className="flex items-center justify-between">
                <span className="me-mono text-[11.5px]">artifact #{r.artifact} · v{r.prompt_version_number}</span>
                <span className={r.is_stale ? 'me-chip me-chip-stale' : 'me-chip me-chip-fresh'}>
                  {r.is_stale ? 'stale' : r.status}
                </span>
              </div>
              <div className="me-mono mt-0.5 text-[10px] me-text-mute">
                {new Date(r.computed_at).toLocaleString()} · {r.latency_ms}ms
              </div>
            </li>
          ))}
          {(recent.data ?? []).length === 0 && <li className="text-[12px] me-text-mute">No results yet.</li>}
        </ul>
      </div>
    </div>
  )
}

// Surfaces the deliberate-stale flow for a single step. Reads outdated
// counts from /api/steps/outdated-counts/, lets the operator add filters
// (date range, matter, artifact ids), previews the count via dry-run,
// and confirms via /api/results/mark-stale/. See
// docs/specs/outdated-vs-stale.md for the why.
function OutdatedPanel({
  step,
  outdated,
  clientCode,
  onChange,
}: {
  step: ComputationStep
  outdated?: StepStaleCount
  clientCode?: string
  onChange: () => void
}) {
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [matterIdsRaw, setMatterIdsRaw] = useState('')
  const [artifactIdsRaw, setArtifactIdsRaw] = useState('')
  const [outdatedOnly, setOutdatedOnly] = useState(true)
  const [preview, setPreview] = useState<{ to_flip: number; total: number } | null>(null)
  const [feedback, setFeedback] = useState<string | null>(null)

  const cr = outdated?.computation_result
  const msr = outdated?.matter_step_result
  const totalOutdated = (cr?.outdated ?? 0) + (msr?.outdated ?? 0)
  const totalStale = (cr?.stale ?? 0) + (msr?.stale ?? 0)
  const totalRows = (cr?.total ?? 0) + (msr?.total ?? 0)

  const buildReq = (dryRun: boolean) => {
    const matter_ids = matterIdsRaw.split(',').map((s) => s.trim()).filter(Boolean).map(Number).filter((n) => !Number.isNaN(n))
    const artifact_ids = artifactIdsRaw.split(',').map((s) => s.trim()).filter(Boolean).map(Number).filter((n) => !Number.isNaN(n))
    return {
      client_code: clientCode || '',
      step_names: [step.name],
      matter_ids: matter_ids.length ? matter_ids : undefined,
      artifact_ids: artifact_ids.length ? artifact_ids : undefined,
      source_date_from: dateFrom || undefined,
      source_date_to: dateTo || undefined,
      outdated_only: outdatedOnly,
      dry_run: dryRun,
    }
  }

  const previewMu = useMutation({
    mutationFn: () => markResultsStale(buildReq(true)),
    onSuccess: (res) => {
      setPreview({
        to_flip: res.to_flip.computation_result + res.to_flip.matter_step_result,
        total: res.counts.computation_result + res.counts.matter_step_result,
      })
      setFeedback(null)
    },
    onError: (e: any) => setFeedback(`preview failed: ${e?.response?.data?.detail || e?.message || 'unknown'}`),
  })
  const confirmMu = useMutation({
    mutationFn: () => markResultsStale(buildReq(false)),
    onSuccess: (res) => {
      const flipped = (res.flipped?.computation_result ?? 0) + (res.flipped?.matter_step_result ?? 0)
      setFeedback(`Marked ${flipped} row${flipped === 1 ? '' : 's'} stale.`)
      setPreview(null)
      onChange()
    },
    onError: (e: any) => setFeedback(`failed: ${e?.response?.data?.detail || e?.message || 'unknown'}`),
  })

  // Hide the panel entirely if there's nothing to act on AND no rows for
  // this step in the tenant — keeps the sidebar clean for unused steps.
  if (totalRows === 0 && totalOutdated === 0 && totalStale === 0) return null

  return (
    <div className="mt-5">
      <div className="me-section-h flex items-center gap-2">
        <Clock size={11} /> Mark for rerun
      </div>
      <div className="me-card p-2.5 space-y-2">
        <div className="flex items-center gap-2 text-[11px]">
          <span className="me-mono">{totalRows}</span>
          <span className="me-text-mute">total</span>
          {totalStale > 0 && (
            <>
              <span className="me-text-mute">·</span>
              <span className="me-chip me-chip-stale">{totalStale} already queued</span>
            </>
          )}
          {totalOutdated > 0 && outdated?.production_version != null && (
            <>
              <span className="me-text-mute">·</span>
              <span className="me-chip" style={{ background: 'var(--me-staleBg)', color: 'var(--me-stale)' }}>
                outdated: {totalOutdated} (current prompt is v{outdated.production_version})
              </span>
            </>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2 text-[11px]">
          <label className="space-y-0.5">
            <span className="me-text-mute">Source from</span>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="me-input me-mono w-full"
            />
          </label>
          <label className="space-y-0.5">
            <span className="me-text-mute">Source to</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="me-input me-mono w-full"
            />
          </label>
          <label className="col-span-2 space-y-0.5">
            <span className="me-text-mute">Matter ids (comma)</span>
            <input
              value={matterIdsRaw}
              onChange={(e) => setMatterIdsRaw(e.target.value)}
              placeholder="14, 50"
              className="me-input me-mono w-full"
            />
          </label>
          <label className="col-span-2 space-y-0.5">
            <span className="me-text-mute">Artifact ids (comma)</span>
            <input
              value={artifactIdsRaw}
              onChange={(e) => setArtifactIdsRaw(e.target.value)}
              placeholder="62289, 62240"
              className="me-input me-mono w-full"
            />
          </label>
          <label className="col-span-2 inline-flex items-center gap-2 text-[11px]">
            <input
              type="checkbox"
              checked={outdatedOnly}
              onChange={(e) => { setOutdatedOnly(e.target.checked); setPreview(null) }}
            />
            Outdated only (skip rows already on current production version)
          </label>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => previewMu.mutate()}
            disabled={previewMu.isPending || !clientCode}
            className="me-btn me-btn-outline"
            title="Dry-run; shows count without flipping flags"
          >
            {previewMu.isPending ? 'Counting…' : 'Preview'}
          </button>
          <button
            onClick={() => confirmMu.mutate()}
            disabled={confirmMu.isPending || !clientCode || !preview || preview.to_flip === 0}
            className="me-btn"
            title="Flip is_stale=True on the previewed rows"
          >
            {confirmMu.isPending ? 'Marking…' : `Mark ${preview?.to_flip ?? 0} stale`}
          </button>
        </div>
        {preview && (
          <div className="text-[11px] me-text-mute">
            Preview: {preview.to_flip} of {preview.total} would flip.
          </div>
        )}
        {feedback && (
          <div className="text-[11px]" style={{ color: 'var(--me-fresh)' }}>{feedback}</div>
        )}
      </div>
    </div>
  )
}

function PromoteRow({ v, step, promote }: { v: PromptVersion; step: ComputationStep; promote: ReturnType<typeof useMutation<{ outdated_count?: number; stale_count?: number }, Error, number, unknown>> }) {
  const { armed } = useArmed()
  return (
    <li className="me-card p-2.5">
      <div className="flex items-center justify-between">
        <div>
          <div className="me-mono text-[12px]">v{v.version}</div>
          <div className="text-[11px] me-text-mute italic truncate max-w-xs">
            {v.commit_message || new Date(v.created_at).toLocaleString()}
          </div>
        </div>
        {armed ? (
          <button
            onClick={() => {
              if (confirmDestructive(
                `Promote v${v.version} to "${step.prompt_version_label}" on ${step.name}?\n\n` +
                `Existing results stay attached to their old version (visible as 'outdated' on this page). ` +
                `They aren't auto-marked stale — use 'Mark outdated as stale' to queue them for rerun.`
              )) promote.mutate(v.id)
            }}
            disabled={promote.isPending}
            className="me-btn me-btn-outline"
            title={`Promote v${v.version} to ${step.prompt_version_label}`}
          >
            <ArrowUpRight size={12} />
            Promote
          </button>
        ) : null}
      </div>
    </li>
  )
}

function computeLayout(steps: ComputationStep[], view: 'horizontal' | 'vertical' = 'horizontal') {
  const vertical = view === 'vertical'
  if (steps.length === 0) return { width: 0, height: 0, levels: 0, nodes: [], edges: [], vertical }
  // Use the slot's level directly (no -1 offset), so L0 (vision pre-pass)
  // sits in column 0 instead of off-canvas at -1. Header below also shows
  // the actual level number.
  const byLevel = new Map<number, ComputationStep[]>()
  for (const s of steps) {
    const lvl = s.level ?? 0
    const arr = byLevel.get(lvl) ?? []
    arr.push(s)
    byLevel.set(lvl, arr)
  }
  const levels = Math.max(...byLevel.keys()) + 1
  const positioned = new Map<number, { x: number; y: number }>()
  const nodes: { step: ComputationStep; x: number; y: number }[] = []
  for (let lvl = 0; lvl < levels; lvl++) {
    const arr = byLevel.get(lvl) ?? []
    arr.forEach((s, idx) => {
      // Horizontal: level → column (x), sibling → row (y).
      // Vertical:   level → row (y),    sibling → column (x). (Ken 2026-08-12)
      const x = vertical ? idx * (NODE_W + X_GAP) : lvl * (NODE_W + X_GAP)
      const y = vertical ? lvl * (NODE_H + Y_GAP) : idx * (NODE_H + Y_GAP)
      positioned.set(s.id, { x, y })
      nodes.push({ step: s, x, y })
    })
  }
  const edges: { parentId: number; childId: number; path: string }[] = []
  for (const s of steps) {
    for (const cid of s.child_ids) {
      const p = positioned.get(s.id)
      const c = positioned.get(cid)
      if (!p || !c) continue
      if (vertical) {
        // Parent bottom-center → child top-center.
        const x1 = p.x + NODE_W / 2
        const y1 = p.y + NODE_H
        const x2 = c.x + NODE_W / 2
        const y2 = c.y
        const my = (y1 + y2) / 2
        edges.push({ parentId: s.id, childId: cid, path: `M${x1},${y1} C${x1},${my} ${x2},${my} ${x2},${y2}` })
      } else {
        // Parent right-center → child left-center.
        const x1 = p.x + NODE_W
        const y1 = p.y + NODE_H / 2
        const x2 = c.x
        const y2 = c.y + NODE_H / 2
        const mx = (x1 + x2) / 2
        edges.push({ parentId: s.id, childId: cid, path: `M${x1},${y1} C${mx},${y1} ${mx},${y2} ${x2},${y2}` })
      }
    }
  }
  const maxRows = Math.max(...Array.from(byLevel.values(), (a) => a.length))
  const width = vertical
    ? maxRows * NODE_W + (maxRows - 1) * X_GAP
    : levels * NODE_W + (levels - 1) * X_GAP
  const height = vertical
    ? levels * NODE_H + (levels - 1) * Y_GAP
    : maxRows * NODE_H + (maxRows - 1) * Y_GAP
  return { width, height, levels, nodes, edges, vertical }
}

function collectAncestors(id: number, steps: ComputationStep[]): Set<number> {
  const byId = new Map(steps.map((s) => [s.id, s]))
  const out = new Set<number>()
  const stack: number[] = [id]
  while (stack.length) {
    const cur = stack.pop()!
    const s = byId.get(cur)
    if (!s) continue
    for (const pid of s.parent_ids) if (!out.has(pid)) { out.add(pid); stack.push(pid) }
  }
  return out
}

function collectDescendants(id: number, steps: ComputationStep[]): Set<number> {
  const byId = new Map(steps.map((s) => [s.id, s]))
  const out = new Set<number>()
  const stack: number[] = [id]
  while (stack.length) {
    const cur = stack.pop()!
    const s = byId.get(cur)
    if (!s) continue
    for (const cid of s.child_ids) if (!out.has(cid)) { out.add(cid); stack.push(cid) }
  }
  return out
}

// ---------- DAG editor (in step-detail pane) ----------

function ArmedDagEditor(props: { step: ComputationStep; steps: ComputationStep[]; onChange: () => void }) {
  // Gate the entire DAG editor on the page's arming switch. When not
  // armed, show a one-line note explaining where the controls went.
  // When armed, show the full editor; the underlying delete still has
  // its own 2-step inline confirm.
  const { armed, isSuperuser } = useArmed()
  if (!isSuperuser) return null
  if (!armed) {
    return (
      <div className="mt-5 me-card p-3 text-[11.5px] me-text-mute italic">
        DAG editor (add/remove steps + edges) hidden. Click <strong>destructive: locked</strong>
        in the page header to arm destructive controls.
      </div>
    )
  }
  return <DagEditor {...props} />
}


function DagEditor({
  step,
  steps,
  onChange,
}: {
  step: ComputationStep
  steps: ComputationStep[]
  onChange: () => void
}) {
  const qc = useQueryClient()
  const slots = useQuery({ queryKey: ['promptSlots'], queryFn: () => slotsApi.list() })
  const allDeps = useQuery({ queryKey: ['stepDeps'], queryFn: () => stepDepsApi.list() })

  const byId = new Map(steps.map((s) => [s.id, s]))

  // Map (parent, child) → dep id, so we know what to DELETE.
  const depIdByEdge = useMemo(() => {
    const m = new Map<string, number>()
    for (const d of allDeps.data ?? []) m.set(`${d.parent}->${d.child}`, d.id)
    return m
  }, [allDeps.data])

  const addEdge = useMutation({
    mutationFn: ({ parent, child }: { parent: number; child: number }) =>
      stepDepsApi.create(parent, child),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stepDeps'] })
      qc.invalidateQueries({ queryKey: ['steps'] })
      onChange()
    },
  })
  const removeEdge = useMutation({
    mutationFn: (depId: number) => stepDepsApi.remove(depId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stepDeps'] })
      qc.invalidateQueries({ queryKey: ['steps'] })
      onChange()
    },
  })
  const removeStep = useMutation({
    mutationFn: () => stepsApi.remove(step.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['steps'] })
      qc.invalidateQueries({ queryKey: ['stepDeps'] })
      onChange()
    },
  })

  const [showAddChild, setShowAddChild] = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  function dropEdge(parentId: number, childId: number) {
    const id = depIdByEdge.get(`${parentId}->${childId}`)
    if (id != null) removeEdge.mutate(id)
  }

  // Eligible parents to add: any step that isn't already a parent + isn't this step + isn't a descendant.
  const descendants = collectDescendants(step.id, steps)
  const eligibleParents = steps.filter(
    (s) => s.id !== step.id && !step.parent_ids.includes(s.id) && !descendants.has(s.id),
  )
  const eligibleChildren = steps.filter(
    (s) => s.id !== step.id && !step.child_ids.includes(s.id) && !collectAncestors(step.id, steps).has(s.id),
  )

  return (
    <div className="mt-4 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <EdgeColumn
          label="Depends on (parents)"
          empty="No parents — root step."
          ids={step.parent_ids}
          byId={byId}
          onRemove={(pid) => dropEdge(pid, step.id)}
        />
        <EdgeColumn
          label="Feeds into (children)"
          empty="No children — leaf step."
          ids={step.child_ids}
          byId={byId}
          onRemove={(cid) => dropEdge(step.id, cid)}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <AddEdgePicker
          label="Add parent"
          steps={eligibleParents}
          onPick={(pid) => addEdge.mutate({ parent: pid, child: step.id })}
          pending={addEdge.isPending}
          error={addEdge.error?.message}
        />
        <AddEdgePicker
          label="Add child"
          steps={eligibleChildren}
          onPick={(cid) => addEdge.mutate({ parent: step.id, child: cid })}
          pending={addEdge.isPending}
          error={addEdge.error?.message}
        />
      </div>

      <div className="me-card p-3">
        <div className="flex items-center justify-between">
          <div className="me-section-h !mb-0">Step actions</div>
          <button
            onClick={() => setShowAddChild((v) => !v)}
            className="me-btn me-btn-outline"
          >
            <Plus size={12} />
            New step from {step.name}
          </button>
        </div>
        {showAddChild && (
          <NewStepForm
            parent={step}
            slots={slots.data ?? []}
            onClose={() => setShowAddChild(false)}
            onCreated={() => {
              setShowAddChild(false)
              onChange()
            }}
          />
        )}
        <div className="mt-2 flex items-center gap-2">
          {!confirmingDelete ? (
            <button
              onClick={() => setConfirmingDelete(true)}
              className="me-btn me-btn-outline text-[color:var(--me-stale)]"
              title={`Delete step ${step.name}`}
            >
              <Trash2 size={12} />
              Delete this step
            </button>
          ) : (
            <>
              <span className="text-[12px] text-[color:var(--me-stale)]">
                Confirm delete? This removes the step + every edge touching it. Existing ComputationResults are kept (orphaned step ref is allowed).
              </span>
              <button
                onClick={() => removeStep.mutate()}
                disabled={removeStep.isPending}
                className="me-btn me-btn-primary"
              >
                Confirm
              </button>
              <button
                onClick={() => setConfirmingDelete(false)}
                className="me-btn me-btn-outline"
              >
                Cancel
              </button>
            </>
          )}
        </div>
        {removeStep.error && (
          <div className="mt-2 text-[11px] text-[color:var(--me-stale)]">
            {(removeStep.error as Error).message}
          </div>
        )}
      </div>
    </div>
  )
}

function EdgeColumn({
  label, empty, ids, byId, onRemove,
}: {
  label: string
  empty: string
  ids: number[]
  byId: Map<number, ComputationStep>
  onRemove: (otherId: number) => void
}) {
  return (
    <div className="me-card p-2.5">
      <div className="me-section-h !mb-1">{label}</div>
      {ids.length === 0 ? (
        <div className="text-[11px] me-text-mute italic">{empty}</div>
      ) : (
        <ul className="space-y-1">
          {ids.map((id) => (
            <li key={id} className="flex items-center justify-between gap-2 rounded-sm bg-[color:var(--me-surfaceHover)] px-2 py-1">
              <span className="me-mono text-[11.5px] truncate">{byId.get(id)?.name ?? id}</span>
              <button
                onClick={() => onRemove(id)}
                className="me-btn me-btn-outline shrink-0"
                title="Remove edge"
              >
                <X size={10} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function AddEdgePicker({
  label, steps, onPick, pending, error,
}: {
  label: string
  steps: ComputationStep[]
  onPick: (id: number) => void
  pending: boolean
  error?: string
}) {
  const [val, setVal] = useState<number | ''>('')
  return (
    <div className="me-card p-2.5">
      <div className="me-section-h !mb-1">{label}</div>
      <div className="flex items-center gap-1">
        <select
          value={val}
          onChange={(e) => setVal(e.target.value ? Number(e.target.value) : '')}
          className="me-input flex-1"
          disabled={steps.length === 0 || pending}
        >
          <option value="">{steps.length === 0 ? 'no eligible steps' : 'choose…'}</option>
          {steps.map((s) => (
            <option key={s.id} value={s.id}>L{s.level} {s.name}</option>
          ))}
        </select>
        <button
          onClick={() => val !== '' && onPick(Number(val))}
          disabled={val === '' || pending}
          className="me-btn me-btn-primary"
          title="Add edge"
        >
          <Plus size={12} />
        </button>
      </div>
      {error && (
        <div className="mt-1 text-[11px] text-[color:var(--me-stale)]">{error}</div>
      )}
    </div>
  )
}

function NewStepForm({
  parent, slots, onClose, onCreated,
}: {
  parent: ComputationStep
  slots: PromptSlot[]
  onClose: () => void
  onCreated: () => void
}) {
  const qc = useQueryClient()
  const [name, setName] = useState('')
  const [slotId, setSlotId] = useState<number | ''>('')
  const [fanoutKind, setFanoutKind] = useState<'none' | 'per_matter' | 'matter_only'>('none')

  const create = useMutation({
    mutationFn: async () => {
      if (slotId === '') throw new Error('pick a slot')
      // Create the step
      const step = await stepsApi.create({
        name: name.trim(),
        slot: slotId,
        prompt_version_label: 'production',
        backend: 'runpod',
        fanout_kind: fanoutKind,
      })
      // Wire it as a child of `parent`
      await stepDepsApi.create(parent.id, step.id)
      return step
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['steps'] })
      qc.invalidateQueries({ queryKey: ['stepDeps'] })
      onCreated()
    },
  })

  // Slots that aren't already used by another step (each slot → 0/1 step in practice).
  const slotsAvailable = slots

  return (
    <div className="mt-3 rounded-sm border me-border p-3">
      <div className="me-section-h !mb-2">
        New step downstream of <span className="me-mono">{parent.name}</span>
      </div>
      <div className="space-y-2">
        <div>
          <label className="text-[11px] me-text-mute">Name (unique)</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. detect_priority_signal"
            className="me-input mt-0.5 w-full"
          />
        </div>
        <div>
          <label className="text-[11px] me-text-mute">Slot (existing — create new slot from /prompts if needed)</label>
          <select
            value={slotId}
            onChange={(e) => setSlotId(e.target.value ? Number(e.target.value) : '')}
            className="me-input mt-0.5 w-full"
          >
            <option value="">choose…</option>
            {slotsAvailable.map((s) => (
              <option key={s.id} value={s.id}>L{s.level} {s.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[11px] me-text-mute">Fan-out</label>
          <select
            value={fanoutKind}
            onChange={(e) => setFanoutKind(e.target.value as typeof fanoutKind)}
            className="me-input mt-0.5 w-full"
          >
            <option value="none">none — one result per artifact</option>
            <option value="per_matter">per_matter — runs once per linked matter</option>
            <option value="matter_only">matter_only — matter-level only</option>
          </select>
        </div>
        <div className="flex justify-end gap-1">
          <button onClick={onClose} className="me-btn me-btn-outline">
            Cancel
          </button>
          <button
            onClick={() => create.mutate()}
            disabled={!name.trim() || slotId === '' || create.isPending}
            className="me-btn me-btn-primary"
          >
            {create.isPending ? 'Creating…' : 'Create + wire'}
          </button>
        </div>
        {create.error && (
          <div className="text-[11px] text-[color:var(--me-stale)]">
            {(create.error as Error).message}
          </div>
        )}
      </div>
    </div>
  )
}
